"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import { Check, ChevronRight, ChevronsUpDown, Grid2X2 } from "lucide-react";

import { useAuth } from "@/components/RequireAuth";
import { Tooltip } from "@/components/ui/tooltip";
import {
  clearPreferredPortal,
  getAccessiblePortals,
  Portal,
  PORTAL_INFO,
  setPreferredPortal,
} from "@/lib/portals";
import { cn } from "@/lib/utils";

const COACHMARK_STORAGE_KEY = "ng.portalSwitcherCoachmarkSeen";

export function PortalSwitcher({
  current,
  className,
}: {
  current: Portal;
  className?: string;
}) {
  const user = useAuth();
  const router = useRouter();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const portals = getAccessiblePortals(user);
  const canSwitch = portals.length > 1;
  const currentInfo = PORTAL_INFO[current];

  const [showCoachmark, setShowCoachmark] = useState(false);
  const coachmarkRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(open);
  const coachmarkTitleId = useId();
  const coachmarkDescId = useId();

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const dismissCoachmark = useCallback(() => {
    setShowCoachmark(false);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(COACHMARK_STORAGE_KEY, "1");
    }
  }, []);

  // First-visit coach-mark: show once, ~1s after mount, unless the user has
  // already dismissed it before or has already found the switcher on their own.
  useEffect(() => {
    if (!canSwitch) return;
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(COACHMARK_STORAGE_KEY)) return;

    const timer = window.setTimeout(() => {
      if (openRef.current) return;
      if (window.localStorage.getItem(COACHMARK_STORAGE_KEY)) return;
      setShowCoachmark(true);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [canSwitch]);

  // If the user opens the real menu themselves, they've discovered the
  // switcher — no need to keep coaching them.
  useEffect(() => {
    if (open) dismissCoachmark();
  }, [open, dismissCoachmark]);

  useEffect(() => {
    if (!showCoachmark) return;

    const onPointerDown = (event: MouseEvent) => {
      if (coachmarkRef.current?.contains(event.target as Node)) return;
      dismissCoachmark();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismissCoachmark();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showCoachmark, dismissCoachmark]);

  useEffect(() => {
    if (!canSwitch) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
      if (event.key?.toLowerCase() === "k" && event.shiftKey && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!open && rect) {
          setMenuPosition({
            left: Math.max(8, Math.min(rect.left, window.innerWidth - 288)),
            top: rect.bottom + 8,
          });
        }
        setOpen((value) => !value);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [canSwitch, open]);

  useEffect(() => {
    if (!open) return;

    const positionMenu = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuPosition({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 288)),
        top: rect.bottom + 8,
      });
    };

    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open]);

  if (!canSwitch) return null;

  const choose = (portal: Portal) => {
    setPreferredPortal(portal);
    setOpen(false);
    if (portal !== current) router.push(PORTAL_INFO[portal].href);
  };

  return (
    <div className={cn("relative z-30", className)}>
      <Tooltip content="Switch portal" side="bottom">
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Switch portal — currently ${currentInfo.label} portal`}
          onClick={() => {
            if (!open) {
              const rect = triggerRef.current?.getBoundingClientRect();
              if (rect) {
                setMenuPosition({
                  left: Math.max(8, Math.min(rect.left, window.innerWidth - 288)),
                  top: rect.bottom + 8,
                });
              }
            }
            setOpen((value) => !value);
          }}
          className="flex h-[30px] cursor-pointer items-center gap-2 rounded-md border border-[#e0d5c6] bg-[#f3ebe1] py-0 pl-1.5 pr-2 text-[13px] text-[#1c1411] transition-colors hover:bg-[#eee3d6] dark:border-sidebar-border dark:bg-sidebar-accent dark:text-sidebar-foreground"
        >
          <span
            aria-hidden="true"
            className="flex h-[18px] w-[18px] items-center justify-center rounded-[4px] text-[9px] font-semibold text-white"
            style={{ backgroundColor: currentInfo.accent }}
          >
            {currentInfo.letter}
          </span>
          <span className="font-medium">{currentInfo.label}</span>
          <span className="text-[11px] text-[#9f9187] dark:text-muted-foreground">portal</span>
          <ChevronsUpDown
            aria-hidden="true"
            className="h-3 w-3 text-[#8e7d72] dark:text-muted-foreground"
          />
        </button>
      </Tooltip>

      {showCoachmark && (
        <div
          ref={coachmarkRef}
          role="dialog"
          aria-labelledby={coachmarkTitleId}
          aria-describedby={coachmarkDescId}
          className="absolute left-0 top-[calc(100%+8px)] z-40 w-[260px] rounded-[10px] border border-[#eee6da] bg-white p-3 text-[#1c1411] shadow-[0_4px_10px_rgba(46,30,22,0.06),0_18px_44px_rgba(46,30,22,0.12)] dark:border-border dark:bg-popover dark:text-popover-foreground"
        >
          <p id={coachmarkTitleId} className="text-[13px] font-semibold">
            Switch portals here
          </p>
          <p id={coachmarkDescId} className="mt-1 text-[12px] leading-snug text-[#5b5049] dark:text-muted-foreground">
            Click your portal badge anytime to jump to another portal you have access to.
          </p>
          <div className="mt-2.5 flex justify-end">
            <button
              type="button"
              onClick={dismissCoachmark}
              className="rounded-md bg-[#673d36] px-2.5 py-1 text-[12px] font-medium text-white transition-colors hover:bg-[#59332d] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#673d36] dark:bg-sidebar-primary dark:hover:opacity-90"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {open &&
        createPortal(
          <>
            <button
              type="button"
              aria-label="Close portal switcher"
              onClick={() => setOpen(false)}
              className="fixed inset-0 cursor-default"
              style={{ zIndex: 2147483646 }}
            />
            <div
              role="menu"
              aria-label="Switch portal"
              className="fixed w-[280px] overflow-y-auto rounded-[10px] border border-[#eee6da] bg-white p-1.5 text-[#1c1411] shadow-[0_4px_10px_rgba(46,30,22,0.06),0_18px_44px_rgba(46,30,22,0.12)] dark:border-border dark:bg-popover dark:text-popover-foreground"
              style={{
                left: menuPosition.left,
                top: menuPosition.top,
                maxHeight: `calc(100vh - ${menuPosition.top + 12}px)`,
                zIndex: 2147483647,
              }}
            >
              <div className="flex h-8 items-center justify-between px-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9c8d84] dark:text-muted-foreground">
                <span>Switch portal</span>
                <kbd className="font-sans text-[10px] font-normal tracking-normal">⌘⇧K</kbd>
              </div>

              <div className="space-y-px">
                {portals.map((portal) => {
                  const info = PORTAL_INFO[portal];
                  const active = portal === current;
                  return (
                    <button
                      key={portal}
                      type="button"
                      role="menuitem"
                      onClick={() => choose(portal)}
                      className={cn(
                        "flex min-h-[52px] w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-[#f7f1ea] dark:hover:bg-accent",
                        active && "bg-[#f3ebe1] dark:bg-sidebar-accent"
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[5px] text-[9px] font-semibold text-white"
                        style={{ backgroundColor: info.accent }}
                      >
                        {info.letter}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium">{info.label}</span>
                        <span className="block text-[11px] text-[#9c8d84] dark:text-muted-foreground">
                          {active ? "Current portal" : "Switch"}
                        </span>
                      </span>
                      {active && <Check className="h-3.5 w-3.5 text-[#673d36] dark:text-sidebar-primary" />}
                    </button>
                  );
                })}
              </div>

              <div className="my-1 h-px bg-[#eee6da] dark:bg-border" />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  clearPreferredPortal();
                  router.push("/select-portal");
                }}
                className="flex h-[37px] w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-[12.5px] font-medium text-[#673d36] transition-colors hover:bg-[#f7f1ea] dark:text-sidebar-primary dark:hover:bg-accent"
              >
                <Grid2X2 className="h-3.5 w-3.5" />
                <span className="flex-1 text-left">Back to portal hub</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
